/**
 * Agent API settings section (Settings → Agent API tab, admin-only).
 *
 * Two stacked cards:
 *   1. API keys — list + create + revoke management for the per-org Agent
 *      API credentials (`agentApiKeys.list/create/revoke`).
 *   2. Documentation — the machine-readable Agent API reference, served by
 *      the backend (`agentApiKeys.getDocs`) so a single source of truth stays
 *      in sync with the live `GET /api/agent/v1/docs` endpoint agents fetch.
 *
 * RBAC (mirrors the canonical ProductDataLookupCard defense-in-depth guard):
 *  - Layer 1 (nav): the "Agent API" tab is only mounted inside Settings.tsx's
 *    admin Tabs block (`isAdmin` gate), so it never appears for non-admins.
 *  - Layer 2 (content): this component returns null when the active user lacks
 *    `org_settings.manage` — Admin + Super Admin hold it; Coordinator/Viewer/
 *    Staff do not. The write controls (Create / Revoke) additionally gate on
 *    `isAdmin()` so they mirror the backend `adminProcedure` even though the
 *    manage-permission gate already implies admin.
 *  - Layer 3/4 (procedure + handler): list/getDocs are `protectedProcedure`
 *    + OrgSettingsRead; create/revoke are `adminProcedure` + requireRole.
 */
import { useState } from 'react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import {
  AlertTriangle,
  Check,
  Clipboard,
  KeyRound,
  Loader2,
  Plus,
  RefreshCw,
  Terminal,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useRole } from '@/hooks/use-role';
import { useDateFnsLocale, DATE_FORMAT } from '@/lib/format-dates';
import { trpc } from '@/lib/trpc';
import { StatusPill } from '@/components/shared/StatusPill';
import { SimpleMarkdown } from './agent-api/SimpleMarkdown';

const COPY_TOAST_ID = 'agent-api-docs-copy';
const REVOKE_TOAST_ID = 'agent-api-key-revoke';
const CREATE_TOAST_ID = 'agent-api-key-create';

/**
 * Mirror of the backend `AGENT_API_SCOPES` (lib/agent-api-keys.ts). Kept as a
 * local const because the frontend can't import backend modules; the create
 * mutation's Zod schema is the runtime source of truth if these ever drift.
 */
const AGENT_API_SCOPES = [
  'purchase_orders:read',
  'comms:write',
  'inventory:read',
  'locations:read',
] as const;
type AgentApiScope = (typeof AGENT_API_SCOPES)[number];
const DEFAULT_SCOPES: AgentApiScope[] = ['purchase_orders:read'];

/**
 * Single source for each scope's i18n keys so adding a backend scope is one
 * entry here + one pair of `settings.json` keys — no per-scope ternary to keep
 * in sync. `label`/`help` resolve to `settings` namespace keys.
 */
const SCOPE_LABEL_KEYS: Record<AgentApiScope, { label: string; help: string }> = {
  'purchase_orders:read': {
    label: 'agentApi.create.scope.purchaseOrdersRead',
    help: 'agentApi.create.scope.purchaseOrdersReadHelp',
  },
  'comms:write': {
    label: 'agentApi.create.scope.commsWrite',
    help: 'agentApi.create.scope.commsWriteHelp',
  },
  'inventory:read': {
    label: 'agentApi.create.scope.inventoryRead',
    help: 'agentApi.create.scope.inventoryReadHelp',
  },
  'locations:read': {
    label: 'agentApi.create.scope.locationsRead',
    help: 'agentApi.create.scope.locationsReadHelp',
  },
};

export default function AgentApiSettings() {
  const { can, isAdmin } = useRole();

  // Layer-2 guard — redundant with the admin Tabs gate, kept for parity with
  // the canonical ProductDataLookupCard pattern (defense in depth).
  if (!can('org_settings.manage')) {
    return null;
  }

  return <AgentApiSettingsInner admin={isAdmin()} />;
}

function AgentApiSettingsInner({ admin }: { admin: boolean }) {
  return (
    <div className="space-y-6">
      <ApiKeysCard admin={admin} />
      <AgentApiDocsCard />
    </div>
  );
}

// ---------------------------------------------------------------------------
// API keys card — list + create + revoke
// ---------------------------------------------------------------------------

function ApiKeysCard({ admin }: { admin: boolean }) {
  const { t } = useTranslation('settings');
  const utils = trpc.useUtils();
  const locale = useDateFnsLocale();

  const keysQuery = trpc.agentApiKeys.list.useQuery(undefined, {
    staleTime: 30_000,
    retry: 1,
  });

  const [createOpen, setCreateOpen] = useState(false);
  const [pendingRevoke, setPendingRevoke] = useState<{ id: string; name: string } | null>(null);

  const revokeMutation = trpc.agentApiKeys.revoke.useMutation({
    onSuccess: () => {
      void utils.agentApiKeys.list.invalidate();
      toast.success(t('agentApi.revoke.success'), { id: REVOKE_TOAST_ID });
      setPendingRevoke(null);
    },
    onError: () => {
      toast.error(t('agentApi.revoke.error'), { id: REVOKE_TOAST_ID, duration: Infinity });
    },
  });

  const keys = keysQuery.data ?? [];
  const hasKeys = keys.length > 0;

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="space-y-1">
              <CardTitle className="flex items-center gap-2">
                <KeyRound className="h-5 w-5 text-threadheads-purple" aria-hidden="true" />
                {t('agentApi.keys.title')}
              </CardTitle>
              <CardDescription>{t('agentApi.keys.description')}</CardDescription>
            </div>
            {admin && (
              <Button
                type="button"
                onClick={() => setCreateOpen(true)}
                className="bg-threadheads-purple text-white hover:bg-threadheads-purple/90"
              >
                <Plus className="mr-1.5 h-4 w-4" aria-hidden="true" />
                {t('agentApi.keys.create')}
              </Button>
            )}
          </div>
        </CardHeader>

        <CardContent>
          {keysQuery.isLoading ? (
            <KeysSkeleton />
          ) : keysQuery.isError ? (
            <KeysError onRetry={() => void keysQuery.refetch()} retrying={keysQuery.isFetching} />
          ) : !hasKeys ? (
            <EmptyKeys admin={admin} onCreate={() => setCreateOpen(true)} />
          ) : (
            <div className="relative overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('agentApi.keys.col.name')}</TableHead>
                    <TableHead>{t('agentApi.keys.col.key')}</TableHead>
                    <TableHead>{t('agentApi.keys.col.scopes')}</TableHead>
                    <TableHead>{t('agentApi.keys.col.created')}</TableHead>
                    <TableHead>{t('agentApi.keys.col.lastUsed')}</TableHead>
                    <TableHead>{t('agentApi.keys.col.status')}</TableHead>
                    {admin && (
                      <TableHead className="text-right">
                        <span className="sr-only">{t('agentApi.keys.col.actions')}</span>
                      </TableHead>
                    )}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {keys.map((key) => {
                    const revoked = key.revokedAt != null;
                    return (
                      <TableRow key={key.id} className={revoked ? 'text-muted-foreground' : undefined}>
                        <TableCell className="font-medium">{key.name}</TableCell>
                        <TableCell>
                          <span className="font-mono text-xs text-muted-foreground">
                            {key.keyPrefix}
                            {'…'}
                          </span>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {key.scopes.map((scope) => (
                              <Badge key={scope} variant="secondary" className="font-mono text-[10px]">
                                {scope}
                              </Badge>
                            ))}
                          </div>
                        </TableCell>
                        <TableCell className="tabular-nums">
                          {format(new Date(key.createdAt), DATE_FORMAT, { locale })}
                        </TableCell>
                        <TableCell className="tabular-nums">
                          {key.lastUsedAt
                            ? format(new Date(key.lastUsedAt), DATE_FORMAT, { locale })
                            : t('agentApi.keys.neverUsed')}
                        </TableCell>
                        <TableCell>
                          <StatusPill tone={revoked ? 'off' : 'on'}>
                            {revoked ? t('agentApi.keys.status.revoked') : t('agentApi.keys.status.active')}
                          </StatusPill>
                        </TableCell>
                        {admin && (
                          <TableCell className="text-right">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="border-orange-300 text-orange-600 hover:bg-orange-50 hover:text-orange-700"
                              disabled={revoked || revokeMutation.isPending}
                              onClick={() => setPendingRevoke({ id: key.id, name: key.name })}
                              aria-label={t('agentApi.keys.revokeAria', { name: key.name })}
                            >
                              {t('agentApi.keys.revoke')}
                            </Button>
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {admin && (
        <CreateKeyDialog
          open={createOpen}
          onOpenChange={setCreateOpen}
          onCreated={() => void utils.agentApiKeys.list.invalidate()}
        />
      )}

      <AlertDialog
        open={pendingRevoke != null}
        onOpenChange={(open) => {
          if (!open && !revokeMutation.isPending) setPendingRevoke(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('agentApi.revoke.title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('agentApi.revoke.description', { name: pendingRevoke?.name ?? '' })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={revokeMutation.isPending}>
              {t('agentApi.revoke.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={revokeMutation.isPending}
              onClick={(event) => {
                // Keep the dialog open while the mutation runs; close on success.
                event.preventDefault();
                if (!pendingRevoke) return;
                revokeMutation.mutate({ id: pendingRevoke.id });
              }}
            >
              {revokeMutation.isPending ? (
                <>
                  <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                  {t('agentApi.revoke.confirm')}
                </>
              ) : (
                t('agentApi.revoke.confirm')
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function EmptyKeys({ admin, onCreate }: { admin: boolean; onCreate: () => void }) {
  const { t } = useTranslation('settings');
  return (
    <div className="flex flex-col items-center gap-3 rounded-md border border-dashed bg-muted/30 px-6 py-10 text-center">
      <KeyRound className="h-6 w-6 text-muted-foreground" aria-hidden="true" />
      <div className="space-y-1">
        <p className="text-sm font-medium text-foreground">{t('agentApi.keys.empty.title')}</p>
        <p className="text-sm text-muted-foreground">{t('agentApi.keys.empty.description')}</p>
      </div>
      {admin && (
        <Button
          type="button"
          onClick={onCreate}
          className="bg-threadheads-purple text-white hover:bg-threadheads-purple/90"
        >
          <Plus className="mr-1.5 h-4 w-4" aria-hidden="true" />
          {t('agentApi.keys.create')}
        </Button>
      )}
    </div>
  );
}

function KeysSkeleton() {
  return (
    <div className="space-y-3" aria-hidden="true">
      {[0, 1, 2].map((i) => (
        <div key={i} className="flex items-center gap-4">
          <Skeleton className="h-5 w-40" />
          <Skeleton className="h-5 w-28" />
          <Skeleton className="h-5 w-24" />
          <Skeleton className="ml-auto h-8 w-20" />
        </div>
      ))}
      <span className="sr-only">Loading API keys…</span>
    </div>
  );
}

function KeysError({ onRetry, retrying }: { onRetry: () => void; retrying: boolean }) {
  const { t } = useTranslation('settings');
  return (
    <div role="alert" className="flex flex-col items-start gap-3 rounded-md border bg-muted/30 p-4">
      <div className="flex items-start gap-2">
        <AlertTriangle className="mt-0.5 h-4 w-4 text-muted-foreground" aria-hidden="true" />
        <p className="text-sm text-foreground">{t('agentApi.keys.loadError')}</p>
      </div>
      <Button type="button" variant="outline" size="sm" onClick={onRetry} disabled={retrying}>
        {retrying ? (
          <>
            <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" aria-hidden="true" />
            {t('agentApi.keys.retry')}
          </>
        ) : (
          <>
            <RefreshCw className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
            {t('agentApi.keys.retry')}
          </>
        )}
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Create key dialog — two phases: form, then one-time key reveal.
// The reveal phase deliberately cannot be dismissed by ESC / overlay click /
// close button (Radix `onEscapeKeyDown` + `onInteractOutside` preventDefault
// + `hideCloseButton`); the only exit is the explicit "I've stored the key"
// button, so the raw key is never lost to an accidental dismissal.
// ---------------------------------------------------------------------------

function CreateKeyDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: () => void;
}) {
  const { t } = useTranslation('settings');

  const [name, setName] = useState('');
  const [scopes, setScopes] = useState<Set<AgentApiScope>>(() => new Set(DEFAULT_SCOPES));
  const [createdKey, setCreatedKey] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const createMutation = trpc.agentApiKeys.create.useMutation({
    onSuccess: (result) => {
      setCreatedKey(result.key);
      onCreated();
    },
    onError: () => {
      toast.error(t('agentApi.create.error'), { id: CREATE_TOAST_ID, duration: Infinity });
    },
  });

  const inReveal = createdKey != null;
  const trimmedName = name.trim();
  const nameValid = trimmedName.length > 0;
  const scopesValid = scopes.size > 0;
  const canSubmit = nameValid && scopesValid && !createMutation.isPending;

  const reset = () => {
    setName('');
    setScopes(new Set(DEFAULT_SCOPES));
    setCreatedKey(null);
    setCopied(false);
    createMutation.reset();
  };

  const requestClose = (next: boolean) => {
    // Reveal phase is locked — dismissal only via the explicit button below.
    if (!next && inReveal) return;
    if (!next) reset();
    onOpenChange(next);
  };

  const toggleScope = (scope: AgentApiScope, checked: boolean) => {
    setScopes((prev) => {
      const nextSet = new Set(prev);
      if (checked) nextSet.add(scope);
      else nextSet.delete(scope);
      return nextSet;
    });
  };

  const handleSubmit = () => {
    if (!canSubmit) return;
    createMutation.mutate({ name: trimmedName, scopes: Array.from(scopes) });
  };

  const handleCopy = async () => {
    if (!createdKey) return;
    try {
      await navigator.clipboard.writeText(createdKey);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2_000);
    } catch {
      toast.error(t('agentApi.reveal.copyError'), { id: CREATE_TOAST_ID });
    }
  };

  const handleDone = () => {
    reset();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={requestClose}>
      <DialogContent
        hideCloseButton={inReveal}
        onEscapeKeyDown={(event) => {
          if (inReveal) event.preventDefault();
        }}
        onInteractOutside={(event) => {
          if (inReveal) event.preventDefault();
        }}
      >
        {inReveal ? (
          <>
            <DialogHeader>
              <DialogTitle>{t('agentApi.reveal.title')}</DialogTitle>
            </DialogHeader>

            <div
              role="alert"
              className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3"
            >
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" aria-hidden="true" />
              <p className="text-sm text-amber-800">{t('agentApi.reveal.warning')}</p>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="agent-api-created-key">{t('agentApi.reveal.keyLabel')}</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="agent-api-created-key"
                  readOnly
                  value={createdKey ?? ''}
                  className="font-mono text-xs"
                  onFocus={(event) => event.currentTarget.select()}
                />
                <Button type="button" variant="outline" onClick={handleCopy} className="shrink-0">
                  {copied ? (
                    <>
                      <Check className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
                      {t('agentApi.reveal.copied')}
                    </>
                  ) : (
                    <>
                      <Clipboard className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
                      {t('agentApi.reveal.copy')}
                    </>
                  )}
                </Button>
              </div>
              <span role="status" aria-live="polite" className="sr-only">
                {copied ? t('agentApi.reveal.copyAnnounce') : ''}
              </span>
            </div>

            <DialogFooter>
              <Button
                type="button"
                onClick={handleDone}
                className="bg-threadheads-purple text-white hover:bg-threadheads-purple/90"
              >
                {t('agentApi.reveal.done')}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>{t('agentApi.create.title')}</DialogTitle>
              <DialogDescription>{t('agentApi.create.description')}</DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="agent-api-key-name">
                  {t('agentApi.create.nameLabel')}
                  <span className="ml-1 text-destructive">*</span>
                </Label>
                <Input
                  id="agent-api-key-name"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  placeholder={t('agentApi.create.namePlaceholder')}
                  maxLength={200}
                  autoFocus
                  className="max-w-prose"
                />
              </div>

              <fieldset className="space-y-2">
                <legend className="text-sm font-medium">
                  {t('agentApi.create.scopesLabel')}
                  <span className="ml-1 text-destructive">*</span>
                </legend>
                {AGENT_API_SCOPES.map((scope) => {
                  const inputId = `agent-api-scope-${scope}`;
                  const { label: labelKey, help: helpKey } = SCOPE_LABEL_KEYS[scope];
                  return (
                    <div key={scope} className="flex items-start gap-2.5">
                      <Checkbox
                        id={inputId}
                        checked={scopes.has(scope)}
                        onCheckedChange={(checked) => toggleScope(scope, checked === true)}
                        className="mt-0.5"
                      />
                      <div className="space-y-0.5">
                        <Label htmlFor={inputId} className="flex items-center gap-2 font-normal">
                          {t(labelKey)}
                          <span className="font-mono text-[10px] text-muted-foreground">{scope}</span>
                        </Label>
                        <p className="text-xs text-muted-foreground">{t(helpKey)}</p>
                      </div>
                    </div>
                  );
                })}
                {!scopesValid && (
                  <p className="text-sm text-destructive">{t('agentApi.create.scopesRequired')}</p>
                )}
              </fieldset>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => requestClose(false)}>
                {t('agentApi.create.cancel')}
              </Button>
              <Button
                type="button"
                onClick={handleSubmit}
                disabled={!canSubmit}
                className="bg-threadheads-purple text-white hover:bg-threadheads-purple/90"
              >
                {createMutation.isPending ? (
                  <>
                    <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                    {t('agentApi.create.submitting')}
                  </>
                ) : (
                  t('agentApi.create.submit')
                )}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Documentation card (unchanged behaviour — the machine-readable reference)
// ---------------------------------------------------------------------------

function AgentApiDocsCard() {
  const docsQuery = trpc.agentApiKeys.getDocs.useQuery(undefined, {
    // The doc changes rarely (ships with the backend). Cache through so a
    // tab-back doesn't re-fetch and flash the skeleton.
    staleTime: 5 * 60_000,
    retry: 1,
  });

  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    const markdown = docsQuery.data?.markdown;
    if (!markdown) return;
    try {
      await navigator.clipboard.writeText(markdown);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2_000);
    } catch {
      toast.error('Copy failed. Select the text manually.', { id: COPY_TOAST_ID });
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="space-y-1">
            <CardTitle className="flex items-center gap-2">
              <Terminal className="h-5 w-5 text-threadheads-purple" aria-hidden="true" />
              Agent API
            </CardTitle>
            <CardDescription>
              Machine-readable API for external ops agents: supplier chasers, integrations.
            </CardDescription>
          </div>

          <div className="flex items-center gap-2">
            {docsQuery.data?.version && (
              <Badge variant="secondary" className="tabular-nums">
                {docsQuery.data.version}
              </Badge>
            )}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleCopy}
              disabled={!docsQuery.data?.markdown}
            >
              {copied ? (
                <>
                  <Check className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
                  Copied
                </>
              ) : (
                <>
                  <Clipboard className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
                  Copy as Markdown
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Decoupled live region so the "Copied" announcement isn't tied to
            the button's static label. */}
        <span role="status" aria-live="polite" className="sr-only">
          {copied ? 'Documentation copied to clipboard.' : ''}
        </span>

        <p className="pt-1 text-xs text-muted-foreground">
          Agents can also fetch this live from{' '}
          <code className="rounded bg-muted px-1 py-0.5 font-mono text-[0.85em] text-foreground">
            GET /api/agent/v1/docs
          </code>{' '}
          with their API key.
        </p>
      </CardHeader>

      <CardContent>
        {docsQuery.isLoading ? (
          <DocsSkeleton />
        ) : docsQuery.isError ? (
          <DocsError onRetry={() => void docsQuery.refetch()} retrying={docsQuery.isFetching} />
        ) : docsQuery.data ? (
          <div className="max-w-3xl">
            <SimpleMarkdown markdown={docsQuery.data.markdown} />
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

function DocsSkeleton() {
  return (
    <div className="space-y-4" aria-hidden="true">
      <Skeleton className="h-6 w-1/3" />
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-11/12" />
      <Skeleton className="h-4 w-4/5" />
      <Skeleton className="h-24 w-full rounded-md" />
      <Skeleton className="h-4 w-2/3" />
      <Skeleton className="h-4 w-3/4" />
      <span className="sr-only">Loading Agent API documentation…</span>
    </div>
  );
}

function DocsError({ onRetry, retrying }: { onRetry: () => void; retrying: boolean }) {
  return (
    <div
      role="alert"
      className="flex flex-col items-start gap-3 rounded-md border bg-muted/30 p-4"
    >
      <div className="flex items-start gap-2">
        <AlertTriangle className="mt-0.5 h-4 w-4 text-muted-foreground" aria-hidden="true" />
        <div className="space-y-0.5">
          <p className="text-sm font-medium text-foreground">Couldn&rsquo;t load the documentation.</p>
          <p className="text-sm text-muted-foreground">
            The Agent API docs are served by the backend. Try again in a moment.
          </p>
        </div>
      </div>
      <Button type="button" variant="outline" size="sm" onClick={onRetry} disabled={retrying}>
        {retrying ? (
          <>
            <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" aria-hidden="true" />
            Retrying…
          </>
        ) : (
          <>
            <RefreshCw className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
            Retry
          </>
        )}
      </Button>
    </div>
  );
}
